import { isPreviewDeployment, isDevelopment, isStaging } from "@tellescope/utils";
import { STAGING_API_URL, TEST_API_URL } from "./api";
import { getSelectedHost } from "@tellescope/ui";

// Legacy portal URL for local development (when using local API)
export const DEV_PORTAL_URL = "http://localhost:3030";

// Hard-coded subdomain used in iframe embed URLs instead of the real org subdomain
const EMBEDDED_SUBDOMAIN = "___embedded___";

/**
 * Extracts the subdomain or custom portal URL from the current hostname.
 * Used for fetching organization info via /organization-info endpoint.
 *
 * URL patterns:
 * - Production: {subdomain}.portal.tellescope.com
 * - Staging: {subdomain}.staging-portal.tellescope.com
 * - Preview: Uses hardcoded "new" subdomain for staging org
 * - Custom domain: Uses customPortalURL (the full origin)
 * - Dev (localhost): Uses customPortalURL with localhost origin
 */
export const getPortalIdentifier = (): { subdomain?: string; customPortalURL?: string } => {
  const hostname = window.location.hostname;

  // Preview deployments (S3/CloudFront PR previews) use a configurable subdomain
  if (isPreviewDeployment()) {
    return { subdomain: import.meta.env.VITE_PREVIEW_SUBDOMAIN ?? "new" };
  }

  // Tellescope-hosted portals (subdomain.portal.tellescope.com or subdomain.staging-portal.tellescope.com)
  if (hostname.includes("tellescope.com")) {
    return { subdomain: hostname.split(".")[0] };
  }

  // Custom portal URL (non-tellescope.com hostname)
  return { customPortalURL: window.location.origin };
};

/**
 * Returns the base URL for the legacy portal based on organization data.
 * Always uses the subdomain-based URL (not customPortalURL) to ensure consistent
 * behavior for embedded iframes and avoid cross-origin issues.
 *
 * In development, this matches the API environment being used (staging API = staging portal).
 */
export const getLegacyPortalURL = (): string => {
  // In development, determine portal URL based on the selected API host
  if (isDevelopment()) {
    const selectedHost = getSelectedHost();

    // Local API = local portal
    if (selectedHost.url === TEST_API_URL) {
      return DEV_PORTAL_URL;
    }

    // Staging API = staging portal
    if (selectedHost.url === STAGING_API_URL) {
      return `https://${EMBEDDED_SUBDOMAIN}.staging-portal.tellescope.com`;
    }

    // Production API = production portal
    return `https://${EMBEDDED_SUBDOMAIN}.portal.tellescope.com`;
  }

  // Staging environment (deployed staging app)
  if (isStaging()) {
    return `https://${EMBEDDED_SUBDOMAIN}.staging-portal.tellescope.com`;
  }

  // Production environment
  return `https://${EMBEDDED_SUBDOMAIN}.portal.tellescope.com`;
};

/**
 * Route for completing a form response within this app, used to keep a Redirect field's form
 * chaining in the portal rather than sending the enduser to the forms deployment.
 */
export const buildFormResponseRoute = (formResponseId: string): string => `/documents/form-responses/${formResponseId}`;

/**
 * Route that opens one form response by its access code — where a form group link lands, and the
 * legacy portal's `/documents/forms/<accessCode>` shape that links already sent still use.
 */
export const buildFormAccessCodeRoute = (accessCode: string): string => `/documents/forms/${accessCode}`;

/**
 * Builds the URL for embedding a form completion in an iframe.
 * This uses the legacy portal's /link/form route.
 * Always uses the subdomain-based URL (not customPortalURL) for consistent embedding.
 */
export const buildFormCompletionURL = (params: {
  organization: { subdomain: string };
  formId: string;
  accessCode: string;
  authToken: string;
  automationStepId?: string;
  hideBg?: boolean;
  startAtNext?: boolean;
}): string => {
  const baseURL = getLegacyPortalURL();
  const searchParams = new URLSearchParams();

  searchParams.set("overrideSubdomain", params.organization.subdomain);
  searchParams.set("f", params.formId);
  searchParams.set("a", params.accessCode);
  searchParams.set("token", params.authToken);

  if (params.automationStepId) {
    searchParams.set("stepId", params.automationStepId);
  }

  if (params.hideBg) {
    searchParams.set("hideBg", "true");
  }

  if (params.startAtNext) {
    searchParams.set("startAtNext", "true");
  }

  return `${baseURL}/link/form?${searchParams.toString()}`;
};

/**
 * Builds the URL for embedding an appointment booking page in an iframe.
 * This uses the legacy portal's /book-an-appointment route.
 * Always uses the subdomain-based URL (not customPortalURL) for consistent embedding.
 *
 * `skipState` suppresses the legacy portal's state-collection step. That step triggers on the
 * enduser's state being absent *client-side*, which is always the case for the restricted sessions
 * used here — so without it the enduser is prompted for a state the backend already knows, and
 * saving it fails: these tokens are scoped to appointment booking and may not write `state`.
 *
 * `state` and `entropy` are only meaningful when relayed from a server-built booking URL
 * (`form_fields.booking_info`); callers minting their own token have neither.
 */
export const buildAppointmentBookingURL = (params: {
  organization: { subdomain: string };
  bookingPageId: string;
  authToken: string;
  userIds?: string[];
  scheduledBy?: string;
  state?: string;
  entropy?: string;
  holdAppointmentMinutes?: string;
  /**
   * When set, the legacy booking page skips host selection, defaults the calendar to
   * tomorrow onward, skips payment collection, and flags this event as rescheduled to the
   * newly booked one once booking completes.
   */
  rescheduledCalendarEventId?: string;
}): string => {
  const baseURL = getLegacyPortalURL();
  const searchParams = new URLSearchParams();

  searchParams.set("overrideSubdomain", params.organization.subdomain);
  searchParams.set("token", params.authToken);
  searchParams.set("embedded", "true");
  searchParams.set("skipState", "true");

  if (params.userIds?.length) {
    searchParams.set("userIds", params.userIds.join(","));
  }

  if (params.scheduledBy) {
    searchParams.set("scheduledBy", params.scheduledBy);
  }

  if (params.state) {
    searchParams.set("state", params.state);
  }

  if (params.entropy) {
    searchParams.set("entropy", params.entropy);
  }

  if (params.holdAppointmentMinutes) {
    searchParams.set("holdAppointmentMinutes", params.holdAppointmentMinutes);
  }

  if (params.rescheduledCalendarEventId) {
    searchParams.set("rescheduledCalendarEventId", params.rescheduledCalendarEventId);
  }

  return `${baseURL}/book-an-appointment/${params.bookingPageId}?${searchParams.toString()}`;
};

// Query params the legacy portal's /appointment-booking-page route reads, from
// tellescope/packages/private/portal/src/pages/AppointmentBooking/AppointmentBooking.tsx:1833-1898,
// contexts.tsx:43 (userId) and AppointmentBooking.tsx:1080 (state).
// `token` is deliberately excluded — the legacy portal treats it as an auth token
// (packages/private/portal/src/App.tsx:604-625), but this route is intentionally unauthenticated
// (see the comment at src/App.tsx:100). Mirrors the existing decision in BookingPageIframe.tsx.
export const FORWARDED_BOOKING_PAGE_PARAMS = [
  "userId",
  "userIds",
  "userTags",
  "userFilterTags",
  "from",
  "to",
  "multi",
  "skipState",
  "fields",
  "bookingToken",
  "rescheduledCalendarEventId",
  "formResponseId",
  "holdAppointmentMinutes",
  "state",
] as const;

/**
 * Builds the URL for embedding the fully public appointment booking page in an iframe.
 * This uses the legacy portal's /appointment-booking-page route, which does not require an
 * authToken — only the org's businessId and the AppointmentBookingPage id.
 * Always uses the placeholder-subdomain + overrideSubdomain pattern (not the real subdomain
 * directly in the hostname) so that CloudFront routes this request to the legacy portal
 * instead of resolving it back to this app.
 *
 * `forwardedParams` is restricted to `FORWARDED_BOOKING_PAGE_PARAMS` — an explicit allowlist
 * rather than blindly forwarding every incoming URL param — and is applied before the four
 * params this builder owns, so an incoming businessId / appointmentBookingPageId / overrideSubdomain
 * / embedded can never override them.
 */
export const buildEmbeddedBookingPageURL = (params: {
  organization: { subdomain: string };
  businessId: string;
  appointmentBookingPageId: string;
  forwardedParams?: URLSearchParams;
}): string => {
  const baseURL = getLegacyPortalURL();
  const searchParams = new URLSearchParams();

  for (const key of FORWARDED_BOOKING_PAGE_PARAMS) {
    const value = params.forwardedParams?.get(key);
    if (value !== null && value !== undefined) {
      searchParams.set(key, value);
    }
  }

  searchParams.set("businessId", params.businessId);
  searchParams.set("appointmentBookingPageId", params.appointmentBookingPageId);
  searchParams.set("overrideSubdomain", params.organization.subdomain);
  searchParams.set("embedded", "true");

  return `${baseURL}/appointment-booking-page?${searchParams.toString()}`;
};
